export interface Me {
  name: string
  email: string
  organization: string | null
  is_external: boolean
  can_view_org_tickets: boolean
  /** Who signs off this person's requests. Null when nobody is recorded — which
   *  is not "no approval needed", it is the state that blocks a request that
   *  needs one. `core.Contact.manager`. */
  manager: { name: string; email: string } | null
}

export interface ServiceRef {
  service: string
  subcategory: string
}

/** When IT is due to respond and resolve. Null when no policy applies.
 *
 *  Deliberately carries no breach flags: the portal serializer does not send
 *  them, because "SLA breached" tells a requester the desk missed its own
 *  target — an internal performance fact dressed as customer information. */
export interface TicketSla {
  policy: string | null
  response_due_at: string | null
  resolution_due_at: string | null
}

export interface TicketSummary {
  ref: string
  type: string
  title: string
  status: string
  priority: string
  impact: string
  urgency: string
  service: ServiceRef | null
  sla: TicketSla | null
  created_at: string
  first_response_at: string | null
  /** Newest case log this contact may see, falling back to creation. Ticket has
   *  no `updated_at`; this is the honest equivalent and the more useful one. */
  last_activity_at: string
  resolved_at: string | null
  closed_at: string | null
}

export interface Attachment {
  id: number
  filename: string
  mime: string
  size_bytes: number
  created_at: string
}

export interface CaseLogEntry {
  id: number
  body_html: string
  author_name: string
  attachments: Attachment[]
  created_at: string
}

/** The manager sign-off on a request, as the *requester* sees it.
 *
 *  Narrower than the approver's view on purpose — the server sends the state,
 *  who holds it and their comment, and nothing that would let a requester act
 *  on their own approval. See `PortalTicketDetailSerializer.get_manager_approval`.
 */
export interface TicketManagerApproval {
  status: ApprovalStatus
  status_label: string
  approver_name: string
  approver_email: string
  comment: string | null
  requested_at: string
  decided_at: string | null
}

export interface TicketDetail extends TicketSummary {
  description: string
  form_data: Record<string, unknown>
  case_logs: CaseLogEntry[]
  attachments: Attachment[]
  can_reopen: boolean
  can_rate: boolean
  csat_score: number | null
  /** Null when this request needed no sign-off, which is most of them. */
  manager_approval: TicketManagerApproval | null
}

/* ------------------------------------------------------------- approvals */

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'cancelled' | 'expired'

/** One approval addressed to the signed-in employee.
 *
 *  `GET /api/portal/approvals/`. The requester's name and email are here
 *  deliberately — you cannot sensibly approve access without knowing whose —
 *  and nothing about the desk's handling is, per invariant 5.
 */
export interface Approval {
  id: number
  ticket_ref: string
  ticket_title: string
  ticket_status: string
  ticket_description: string
  form_data: Record<string, unknown>
  requester_name: string
  requester_email: string
  service: ServiceRef | null
  organization: string
  status: ApprovalStatus
  decision: string | null
  comment: string
  requested_at: string
  decided_at: string | null
  expires_at: string | null
  decided_by_name: string | null
}

export const APPROVAL_STATUS_LABELS: Record<ApprovalStatus, string> = {
  pending: 'Waiting on you',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Withdrawn',
  // Never an approval. An expiry that read as "approved" would be the worst
  // possible default here, so it is labelled as the refusal it is.
  expired: 'Expired undecided',
}

export interface SchemaField {
  name: string
  label?: string
  type?: 'text' | 'textarea' | 'select' | 'number' | 'date' | 'checkbox'
  required?: boolean
  options?: string[]
}

export interface CatalogItem {
  id: number
  name: string
  service: string
  form_schema: { fields?: SchemaField[] }
  /** Minutes, or null when the subcategory has no default SLA policy. The card
   *  says nothing rather than inventing a target nobody promised. */
  response_mins: number | null
  resolution_mins: number | null
}

export interface ArticleSummary {
  slug: string
  title: string
  updated_at: string
}

export interface Article extends ArticleSummary {
  body_md: string
}

export const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  in_progress: 'In progress',
  pending_customer: 'Awaiting your reply',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled',
}

export const PRIORITY_LABELS: Record<string, string> = {
  p1: 'P1 Critical',
  p2: 'P2 High',
  p3: 'P3 Medium',
  p4: 'P4 Low',
}
